import { Trans, useTranslation } from 'react-i18next';

import { App } from 'antd';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAppStore } from '../../store/store';
import { useAuth } from '../useAuth';
import { useFileUpload } from '../useFileUpload';
import useOpenFile from '../useOpenFile';
import { usePermissions } from '../usePermissions';
import { useTags } from '../useTags';
import { useFilesAiActions } from './ai/useFilesAiActions';
import type { UseFilesActionsDeps } from './deps';
import { useFilesCrudActions } from './crud/useFilesCrudActions';
import { useFilesNoteActions } from './notes/useFilesNoteActions';
import { useFilesPasswordActions } from './security/useFilesPasswordActions';
import { useFilesShareActions } from './share/useFilesShareActions';
import { useFilesTrashActions } from './trash/useFilesTrashActions';
import type { UseFilesActionsProps, UseFilesActionsReturn } from './types';

export type { UseFilesActionsEvents } from './types';

/**
 * Composes folder-scoped file operations (CRUD, sharing, trash, AI, and password flows)
 * from focused hooks while keeping a single stable dependency snapshot for child hooks.
 */
export function useFilesActions({ folderUid }: UseFilesActionsProps): UseFilesActionsReturn {
  const { t } = useTranslation();
  const { modal, notification, message } = App.useApp();
  const folderUidRef = useRef(folderUid);
  const setIsModalLoading = useAppStore((s) => s.setIsModalLoading);
  const token = useAppStore((s) => s.token);
  const refreshToken = useAppStore((s) => s.customTokens?.rToken);
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { isAdminOrContributor, authenticatedUser } = useAuth();
  const { decreaseReceivedUnreadCountBy, increaseReceivedUnreadCountBy } = usePermissions();
  const { uploadFiles, onNewFileUploaded, offNewFileUploaded } = useFileUpload();
  const { tags } = useTags();
  const { redirectFileToLocation } = useOpenFile();
  const getFilePasswordSession = useAppStore((s) => s.getFilePasswordSession);
  const removeFilePasswordSession = useAppStore((s) => s.removeFilePasswordSession);
  const isModalLoading = useAppStore((s) => s.isModalLoading);

  const deps = useMemo((): UseFilesActionsDeps => {
    return {
      folderUidRef,
      t,
      Trans,
      modal,
      notification,
      message,
      navigate,
      location,
      params,
      token,
      refreshToken,
      authenticatedUser,
      isAdminOrContributor,
      uploadFiles,
      onNewFileUploaded,
      offNewFileUploaded,
      tags,
      redirectFileToLocation,
      getFilePasswordSession,
      removeFilePasswordSession,
      isModalLoading,
      setIsModalLoading,
      decreaseReceivedUnreadCountBy,
      increaseReceivedUnreadCountBy,
    };
  }, [
    folderUidRef,
    t,
    modal,
    notification,
    message,
    navigate,
    location,
    params,
    token,
    refreshToken,
    authenticatedUser,
    isAdminOrContributor,
    uploadFiles,
    onNewFileUploaded,
    offNewFileUploaded,
    tags,
    redirectFileToLocation,
    getFilePasswordSession,
    removeFilePasswordSession,
    isModalLoading,
    setIsModalLoading,
    decreaseReceivedUnreadCountBy,
    increaseReceivedUnreadCountBy,
  ]);

  const trash = useFilesTrashActions(deps);
  const share = useFilesShareActions(deps);
  const ai = useFilesAiActions(deps);
  const security = useFilesPasswordActions(deps);
  const notes = useFilesNoteActions(deps);
  const crud = useFilesCrudActions(deps);

  useEffect(() => {
    folderUidRef.current = folderUid;
  }, [folderUid]);

  return {
    ...crud,
    ...trash,
    ...share,
    ...ai,
    ...security,
    ...notes,
  };
}

export default useFilesActions;
